import {InputAdornment, OutlinedInput} from "@mui/material";
import {t} from "i18next";
import {useEffect} from "react";
import {Field, FormikProps, useFormikContext} from "formik";
import {WeightUnit} from "../../../models";
import {convertToUnit} from "../weight-input";

export interface WeightInputProps {
    units?: WeightUnit[];
    onChange?: any;
    weightLimit?: string;
    weightLimitUnit?: WeightUnit;
}

export const formatWeight = ({value, unit, weightLimit, weightLimitUnit, targetUnit, decimalPoints = 2}: {
    value: string,
    unit: WeightUnit,
    weightLimit?: string,
    weightLimitUnit?: WeightUnit,
    targetUnit: WeightUnit,
    decimalPoints?: number
}) => {
    let formatted = value
        .replace(/[^0-9.]+/g, '')
        .replace(',', '.')
        .replace(/\.(?=.*\.)/g, '')
        .slice(0, 6)

    if (unit != targetUnit) {
        formatted = convertToUnit(formatted, unit, targetUnit).toFixed(decimalPoints)
    }

    if (weightLimit && weightLimitUnit) {
        const weightLimitConverted = convertToUnit(weightLimit, weightLimitUnit, targetUnit);

        if (+formatted > weightLimitConverted) {
            formatted = weightLimitConverted.toFixed(decimalPoints)
        }
    }

    return formatted
}

export default function WeightInputFormik({
                                              units = [WeightUnit.Kg, WeightUnit.Lb],
                                              onChange,
                                              weightLimit = "50.00",
                                              weightLimitUnit = WeightUnit.Kg
                                          }: WeightInputProps) {

    const formik = useFormikContext<any>();

    const handleWeightOnChange = async (formik: FormikProps<any>) => {
        if (onChange)
            onChange(formik)
    }

    const handleWeightUnitOnChange = async (formik: FormikProps<any>) => {
        if (onChange)
            onChange(formik)
    }

    const weightLimitConverted = formatWeight({
        value: weightLimit,
        unit: weightLimitUnit,
        targetUnit: formik.values.weightUnit,
        decimalPoints: 0
    });

    useEffect(() => {
        formik.setFieldValue("weight", formatWeight({
            value: formik.values.weight?.toString() ?? '',
            unit: formik.values.weightUnit,
            weightLimit,
            weightLimitUnit,
            targetUnit: formik.values.weightUnit
        }))
    }, [formik?.values?.weightUnit]);

    return <Field
        name={"weight"}
        render={() =>
            <OutlinedInput
                sx={{width: "100%"}} inputProps={{inputMode: 'numeric'}}
                placeholder={t(`weight.placeholder`, {
                    weightLimit: weightLimitConverted,
                    weightLimitUnit: t(`weight.${formik.values.weightUnit?.toLowerCase()}`)
                })}
                value={formik?.values?.weight}
                onBlur={() => formik.setFieldTouched("weight", true)}
                onChange={async (event) => {
                    await formik.setFieldValue("weight", formatWeight({
                        value: event?.target?.value,
                        unit: formik.values.weightUnit,
                        weightLimit,
                        weightLimitUnit,
                        targetUnit: formik.values.weightUnit
                    }))
                    await formik.setFieldTouched("weight", true)
                    await handleWeightOnChange(formik)
                }}
                error={!!formik.errors.weight && !!formik.touched.weight}
                endAdornment={
                    <InputAdornment position="end">
                        {units?.map((unit) => {
                            return <div
                                className={`weight-unit ${formik.values.weightUnit === unit ? 'selected' : ''} ${!!formik.errors.weightUnit ? 'error' : ''}`}
                                onClick={async () => {
                                    await formik.setFieldValue("weightUnit", unit)
                                    await formik.setFieldTouched("weightUnit", true)
                                    await handleWeightUnitOnChange(formik)
                                }}>{t(`weight.${unit.toLowerCase()}`)}
                            </div>
                        })
                        }
                    </InputAdornment>
                }
            />
        }
    />

}